import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Briefcase, FolderKanban, Users, ChevronDown, Check } from "lucide-react";
import { cn } from "@/modules/shared/lib/utils";
import { useModuleAccess, type ModuleKey } from "@/modules/shared/hooks/useModuleAccess";

const MODULE_LABELS: Record<string, string> = {
  talent: "Talent",
  sales: "Sales",
  operations: "Operations",
};

const MODULE_ICONS: Record<string, React.ElementType> = {
  talent: Users,
  sales: Briefcase,
  operations: FolderKanban,
};

const MODULE_HOME: Record<string, string> = {
  talent: "/dashboard",
  sales: "/dashboard",
  operations: "/dashboard",
};

const MODULE_COLORS: Record<string, string> = {
  talent: "bg-violet-500",
  sales: "bg-emerald-500",
  operations: "bg-sky-500",
};

interface ModuleSwitcherProps {
  collapsed?: boolean;
}

export function ModuleSwitcher({ collapsed = false }: ModuleSwitcherProps) {
  const navigate = useNavigate();
  const { userModules, currentModule, setCurrentModule } = useModuleAccess();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  if (userModules.length <= 1) return null;

  const CurrentIcon = MODULE_ICONS[currentModule] ?? FolderKanban;
  const currentLabel = MODULE_LABELS[currentModule] ?? currentModule;
  const currentColor = MODULE_COLORS[currentModule] ?? "bg-primary";

  const handleSelect = (mod: ModuleKey) => {
    if (mod !== currentModule) {
      setCurrentModule(mod);
      navigate(MODULE_HOME[mod] ?? "/dashboard");
    }
    setOpen(false);
  };

  if (collapsed) {
    return (
      <div className="relative px-1 pb-3" data-testid="module-switcher" ref={ref}>
        <button
          onClick={() => setOpen(!open)}
          className={cn(
            "relative w-full h-10 flex items-center justify-center rounded-lg transition-all duration-150",
            "bg-sidebar-accent/30 hover:bg-sidebar-accent/50 text-sidebar-foreground"
          )}
          aria-label={`Trocar módulo (atual: ${currentLabel})`}
          aria-expanded={open}
          title={currentLabel}
        >
          <CurrentIcon className="h-4 w-4" />
          <span
            className={cn(
              "absolute top-1 right-1 h-1.5 w-1.5 rounded-full",
              currentColor
            )}
          />
        </button>

        {open && (
          <div className="absolute left-full top-0 z-50 ml-2 min-w-[180px] rounded-lg border border-sidebar-border/50 bg-sidebar shadow-lg shadow-black/20 py-1 animate-in fade-in-0 zoom-in-95 duration-100">
            {userModules.map((mod) => {
              const Icon = MODULE_ICONS[mod] ?? FolderKanban;
              const label = MODULE_LABELS[mod] ?? mod;
              const color = MODULE_COLORS[mod] ?? "bg-primary";
              const isActive = mod === currentModule;
              return (
                <button
                  key={mod}
                  onClick={() => handleSelect(mod)}
                  className={cn(
                    "w-full flex items-center gap-2.5 px-3 py-2 text-[13px] font-medium transition-colors",
                    isActive
                      ? "text-sidebar-foreground bg-sidebar-accent/40"
                      : "text-sidebar-foreground/60 hover:text-sidebar-foreground hover:bg-sidebar-accent/30"
                  )}
                >
                  <span className={cn("h-2 w-2 rounded-full shrink-0", color)} />
                  <Icon className="h-4 w-4 shrink-0" />
                  <span className="flex-1 text-left truncate">{label}</span>
                  {isActive && <Check className="h-3.5 w-3.5 text-sidebar-foreground/50" />}
                </button>
              );
            })}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="relative px-1 pb-3" data-testid="module-switcher" ref={ref}>
      <button
        onClick={() => setOpen(!open)}
        className={cn(
          "w-full flex items-center gap-2.5 rounded-lg px-3 py-2 text-[13px] font-medium transition-all duration-150",
          "bg-sidebar-accent/30 hover:bg-sidebar-accent/50 text-sidebar-foreground"
        )}
        aria-label="Trocar módulo"
        aria-expanded={open}
      >
        <span className={cn("h-2 w-2 rounded-full shrink-0", currentColor)} />
        <CurrentIcon className="h-4 w-4 shrink-0 text-sidebar-foreground/70" />
        <span className="flex-1 text-left truncate">{currentLabel}</span>
        <ChevronDown
          className={cn(
            "h-3.5 w-3.5 text-sidebar-foreground/40 transition-transform duration-200",
            open && "rotate-180"
          )}
        />
      </button>

      {open && (
        <div className="absolute left-1 right-1 top-full z-50 mt-1 rounded-lg border border-sidebar-border/50 bg-sidebar shadow-lg shadow-black/20 py-1 animate-in fade-in-0 zoom-in-95 duration-100">
          {userModules.map((mod) => {
            const Icon = MODULE_ICONS[mod] ?? FolderKanban;
            const label = MODULE_LABELS[mod] ?? mod;
            const color = MODULE_COLORS[mod] ?? "bg-primary";
            const isActive = mod === currentModule;

            return (
              <button
                key={mod}
                onClick={() => handleSelect(mod)}
                className={cn(
                  "w-full flex items-center gap-2.5 px-3 py-2 text-[13px] font-medium transition-colors",
                  isActive
                    ? "text-sidebar-foreground bg-sidebar-accent/40"
                    : "text-sidebar-foreground/60 hover:text-sidebar-foreground hover:bg-sidebar-accent/30"
                )}
              >
                <span className={cn("h-2 w-2 rounded-full shrink-0", color)} />
                <Icon className="h-4 w-4 shrink-0" />
                <span className="flex-1 text-left truncate">{label}</span>
                {isActive && <Check className="h-3.5 w-3.5 text-sidebar-foreground/50" />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

export { MODULE_LABELS, MODULE_ICONS, MODULE_HOME };
